import { useState } from 'react';
import {
  Alert,
  Button,
  FileButton,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Link, Navigate, useParams } from 'react-router';
import { DOKUMAN_RAF_SEKMELERI, rafSekmesiSchema } from '@isg/validation';
import type { RafSekmesi } from '@isg/validation';
import {
  bosRafSuzgeci,
  etkinRafSuzgeciSayisi,
  useRafBelgeleri,
  useRafBelgesiIndir,
  useRafBelgesiSil,
  useRafBelgesiYukle,
  useWorkplacesTumu,
} from '../api/queries';
import type { RafListeSuzgeciUi } from '../api/queries';
import { tarihGoster, zamanGoster } from '../lib/format';
import { ModulSayfasi } from '../shell/ModulSayfasi';
import type { Sekme } from '../shell/ModulSayfasi';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { Rozet, SatirEylemleri, Sayfalama, SolukSatir, SuzgecGostergesi } from '../sozlesme';
import { ANLAM } from '../theme';
import { BosListeNotu, QueryStatus } from './parts';

/**
 * DOKÜMANLAR — V1 SADE RAF (Kapsam §31/K1; sözleşme P1-a + şerhi · P2 · P4 ·
 * P11 · M1).
 *
 * Altı sekme ROTA TABANLI (`/dokumanlar/:sekme`); altlarında şirketin
 * yüklediği PDF'ler: ad (= dosya adı) · işyeri · yükleyen · yükleme tarihi —
 * BU KADAR. Plan sekmesi acil durum planını ve krokiyi DE listeler (K2 —
 * tek kayıt iki yüzey): o satırlar *Acil durum* rozeti + sayfasına bağlantı
 * taşır ve **silme eylemi çizilmez** (yazma yüzeyi acil durum sayfası).
 *
 * ⚠️ **HİÇBİR YETKİ EKRANDA HESAPLANMAZ** (§2.40): E1 tetikleyicisi
 * `yetkiler.yazabilir`, satır silme `silinebilir`, yükleme satırının işyeri
 * listesi `yuklenebilirIsyerleri` — üçü de sunucudan.
 * ⚠️ **P1-a ŞERHİ:** liste sayfalanır → soluk satır listenin BAŞINDA,
 * üstteki *"Belge yükle"* düğmesiyle açılır; iki soluk satır aynı anda yok.
 * ⚠️ **K4 / M1:** *Form* sekmesi form motoru değildir — açıklama cümlesi
 * YAZILMAZ; doğru yerde durması yeter.
 */
const SAYFA_BOYUTU = 25;

const DOKUMAN_SEKMELERI: Sekme[] = DOKUMAN_RAF_SEKMELERI.map((sekme) => ({
  key: `dokumanlar-${sekme}`,
  path: `/dokumanlar/${sekme}`,
}));

export function DokumanlarScreen(): React.JSX.Element {
  const { sekme } = useParams<{ sekme: string }>();
  const cozum = rafSekmesiSchema.safeParse(sekme);
  // Tanınmayan sekme adresi ilk sekmeye düşer — boş ekran ya da hata değil.
  if (!cozum.success) return <Navigate to="/dokumanlar/plan" replace />;
  return (
    <ModulSayfasi
      testId="dokuman-modul-sekmeleri"
      sekmeler={DOKUMAN_SEKMELERI}
      aktif={`dokumanlar-${cozum.data}`}
    >
      <RafSekmesiIcerigi key={cozum.data} sekme={cozum.data} />
    </ModulSayfasi>
  );
}

function RafSekmesiIcerigi({ sekme }: { sekme: RafSekmesi }): React.JSX.Element {
  const isyerleri = useWorkplacesTumu();
  const [suzgec, setSuzgec] = useState<RafListeSuzgeciUi>(() => bosRafSuzgeci(sekme));
  const liste = useRafBelgeleri(suzgec);
  const yukle = useRafBelgesiYukle();
  const sil = useRafBelgesiSil();
  const indir = useRafBelgesiIndir();
  const [yuklemeAcik, setYuklemeAcik] = useState(false);
  const [dosya, setDosya] = useState<File | null>(null);
  const [isyeriId, setIsyeriId] = useState<string | null>(null);
  // D2-3: Diğer sekmesinde serbest belge adı ZORUNLU (öteki sekmelerde ad = dosya adı).
  const serbestAd = sekme === 'diger';
  const [belgeAdi, setBelgeAdi] = useState('');
  // P2: GBF sekmesinde kimyasal adı ZORUNLU + tedarikçinin düzenleme tarihi (opsiyonel;
  // dış belgenin tarihi — "bugün" varsayılanı YOK, raf revizyonu değil).
  const gbf = sekme === 'gbf';
  const [kimyasalAdi, setKimyasalAdi] = useState('');
  const [gbfTarihi, setGbfTarihi] = useState('');
  const hata = yukle.error ?? sil.error;

  function suzgeciDegistir(yama: Partial<RafListeSuzgeciUi>): void {
    setSuzgec((eski) => ({ ...eski, ...yama, sayfa: 1 }));
  }

  const satirlar = liste.data?.satirlar ?? [];
  const toplam = liste.data?.toplam ?? 0;
  const yazabilir = liste.data?.yetkiler.yazabilir ?? false;
  const yuklenebilir = liste.data?.yuklenebilirIsyerleri ?? [];
  // Tek yazılabilir işyeri varsa seçim sorulmaz — satır onunla açılır.
  const seciliIsyeri = isyeriId ?? (yuklenebilir.length === 1 ? yuklenebilir[0]!.id : null);
  const dokunuldu =
    dosya !== null ||
    isyeriId !== null ||
    belgeAdi !== '' ||
    kimyasalAdi !== '' ||
    gbfTarihi !== '';
  const kaydedilebilir =
    dosya !== null &&
    seciliIsyeri !== null &&
    (!serbestAd || belgeAdi.trim().length > 0) &&
    (!gbf || kimyasalAdi.trim().length > 0);
  // P12 (madde 224) — D2-3 serbest ad metin taşır: kaydedilmemiş satır çıkışta uyarır.
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'belge satırı');

  function kaydet(): void {
    if (!dosya || !seciliIsyeri) return;
    yukle.mutate(
      {
        dosya,
        tip: sekme,
        workplaceId: seciliIsyeri,
        ...(serbestAd ? { ad: belgeAdi.trim() } : {}),
        ...(gbf
          ? {
              kimyasalAdi: kimyasalAdi.trim(),
              ...(gbfTarihi ? { gbfDuzenlemeTarihi: gbfTarihi } : {}),
            }
          : {}),
      },
      {
        onSuccess: () => {
          setDosya(null);
          setIsyeriId(null);
          setBelgeAdi('');
          setKimyasalAdi('');
          setGbfTarihi('');
          setYuklemeAcik(false);
        },
      },
    );
  }

  return (
    <Stack data-testid={`dokuman-raf-${sekme}`}>
      <Group justify="space-between" align="flex-end">
        <Title order={4}>Dokümanlar</Title>
        <Group align="flex-end">
          <Select
            data-testid="dokuman-isyeri-suzgeci"
            label="İşyeri"
            placeholder="Tümü"
            clearable
            searchable
            w={240}
            value={suzgec.workplaceId}
            onChange={(deger) => suzgeciDegistir({ workplaceId: deger })}
            data={(isyerleri.data ?? []).map((w) => ({ value: w.id, label: w.ad }))}
          />
          <TextInput
            data-testid="dokuman-ara-suzgeci"
            label="Ad"
            placeholder="Dosya adı"
            w={220}
            value={suzgec.ara}
            onChange={(e) => suzgeciDegistir({ ara: e.currentTarget.value })}
          />
          {/* E1 tetikleyicisi — görünürlük SUNUCUDAN (yetkiler.yazabilir). */}
          {yazabilir && (
            <Button
              data-testid="dokuman-yukle-ac"
              variant={yuklemeAcik ? 'default' : 'filled'}
              onClick={() => setYuklemeAcik((a) => !a)}
            >
              {yuklemeAcik ? 'Vazgeç' : 'Belge yükle'}
            </Button>
          )}
        </Group>
      </Group>

      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="dokuman-hata">
          {hata.message}
        </Alert>
      )}

      <QueryStatus queries={[isyerleri, liste]} />
      <SuzgecGostergesi
        etkinSayisi={etkinRafSuzgeciSayisi(suzgec)}
        onTemizle={() => setSuzgec(bosRafSuzgeci(sekme))}
      />

      {liste.isSuccess && toplam === 0 && !yuklemeAcik && (
        <BosListeNotu
          mesaj={
            etkinRafSuzgeciSayisi(suzgec) > 0
              ? 'Bu süzgeçlerle eşleşen belge yok — süzgeçleri temizleyebilirsiniz.'
              : 'Bu sekmede belge yok.'
          }
        />
      )}

      {liste.isSuccess && (toplam > 0 || yuklemeAcik) && (
        <>
          <Table striped highlightOnHover data-testid="dokuman-listesi">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ad</Table.Th>
                <Table.Th w={220}>İşyeri</Table.Th>
                <Table.Th w={180}>Yükleyen</Table.Th>
                <Table.Th w={160}>Yükleme tarihi</Table.Th>
                <Table.Th w={60} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {/* P1-a şerhi: sayfalanan listede soluk satır BAŞTA, tetikleyiciyle açılır. */}
              {yuklemeAcik && yazabilir && (
                <SolukSatir
                  testId="dokuman-soluk"
                  dokunuldu={dokunuldu}
                  kaydedilebilir={kaydedilebilir}
                  kaydediliyor={yukle.isPending}
                  kolonSayisi={4}
                  onKaydet={kaydet}
                >
                  <Table.Td>
                    {serbestAd && (
                      <TextInput
                        size="xs"
                        mb={4}
                        aria-label="Belge adı"
                        placeholder="Belge adı"
                        data-testid="dokuman-ad"
                        value={belgeAdi}
                        onChange={(e) => setBelgeAdi(e.currentTarget.value)}
                      />
                    )}
                    {gbf && (
                      <Group gap="xs" mb={4} wrap="nowrap">
                        <TextInput
                          size="xs"
                          aria-label="Kimyasal adı"
                          placeholder="Kimyasal adı"
                          data-testid="dokuman-kimyasal-adi"
                          value={kimyasalAdi}
                          onChange={(e) => setKimyasalAdi(e.currentTarget.value)}
                        />
                        <TextInput
                          size="xs"
                          type="date"
                          aria-label="GBF düzenleme tarihi"
                          data-testid="dokuman-gbf-tarihi"
                          value={gbfTarihi}
                          onChange={(e) => setGbfTarihi(e.currentTarget.value)}
                        />
                      </Group>
                    )}
                    <FileButton onChange={setDosya} accept="application/pdf">
                      {(props) => (
                        <Button
                          {...props}
                          size="compact-xs"
                          variant="light"
                          data-testid="dokuman-dosya"
                        >
                          {dosya ? dosya.name : '+ PDF seç'}
                        </Button>
                      )}
                    </FileButton>
                  </Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      aria-label="İşyeri"
                      placeholder="İşyeri"
                      comboboxProps={{ withinPortal: true }}
                      value={seciliIsyeri}
                      onChange={(v) => setIsyeriId(v)}
                      data={yuklenebilir.map((w) => ({ value: w.id, label: w.ad }))}
                      data-testid="dokuman-isyeri"
                    />
                  </Table.Td>
                  <Table.Td colSpan={2}>
                    <Text size="xs" c="dimmed">
                      Yükleyen ve tarih kaydedilince yazılır.
                    </Text>
                  </Table.Td>
                </SolukSatir>
              )}
              {satirlar.map((s) => (
                <Table.Tr key={s.id} data-testid={`dokuman-satiri-${s.id}`}>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        data-testid={`dokuman-indir-${s.id}`}
                        onClick={() =>
                          void indir(s.id).then(({ blob, dosyaAdi }) => {
                            const url = URL.createObjectURL(blob);
                            const bag = document.createElement('a');
                            bag.href = url;
                            bag.download = dosyaAdi ?? s.ad;
                            document.body.appendChild(bag);
                            bag.click();
                            bag.remove();
                            URL.revokeObjectURL(url);
                          })
                        }
                      >
                        {s.ad}
                      </Button>
                      {/* P2 — GBF satırı: kimyasal adı (+ varsa düzenleme tarihi) adın yanında. */}
                      {s.kimyasalAdi && (
                        <Text size="xs" c="dimmed" data-testid={`dokuman-gbf-${s.id}`}>
                          {s.kimyasalAdi}
                          {s.gbfDuzenlemeTarihi ? ` · ${tarihGoster(s.gbfDuzenlemeTarihi)}` : ''}
                        </Text>
                      )}
                      {/* K2 — tek kayıt iki yüzey: tekil (acil durum) belge rafta salt okunur. */}
                      {s.yapi === 'tekil' && (
                        <Rozet anlam="bilgi" testId={`dokuman-acil-durum-${s.id}`}>
                          <Link to={`/acil-durum/${s.workplaceId}`}>{s.tipEtiketi}</Link>
                        </Rozet>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>{s.workplaceAd}</Table.Td>
                  <Table.Td>{s.yukleyenEtiket}</Table.Td>
                  <Table.Td>{zamanGoster(s.yuklemeTarihi)}</Table.Td>
                  <Table.Td>
                    {/* `silinebilir` SUNUCUDAN — tekil tipte ve bahçe dışında eylem ÇİZİLMEZ. */}
                    {s.silinebilir && (
                      <SatirEylemleri
                        testId={`dokuman-eylemler-${s.id}`}
                        digerleri={[
                          {
                            etiket: 'Belgeyi sil',
                            tehlikeli: true,
                            testId: `dokuman-sil-${s.id}`,
                            onClick: () => sil.mutate({ id: s.id }),
                          },
                        ]}
                      />
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {toplam > 0 && (
            <Sayfalama
              sayfa={suzgec.sayfa}
              sayfaBoyutu={SAYFA_BOYUTU}
              toplam={toplam}
              onDegis={(sayfa) => setSuzgec((eski) => ({ ...eski, sayfa }))}
              testId="dokuman-sayfalama"
            />
          )}
        </>
      )}
    </Stack>
  );
}
